import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarToday"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import ViewCompactIcon from "@mui/icons-material/ViewCompact"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import { addHours, format, formatISO } from "date-fns"
import { useEffect, useRef, useState } from "react"

import FullCalendar from "@fullcalendar/react"

import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"
import timeGridPlugin from "@fullcalendar/timegrid"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Row, { RowButColumnOnMobile } from "../../../common/components/Row"
import { useIsMobile } from "../../../common/utils"
import "../../../static/css/calendar.scss"
import EventPopover from "./EventPopover"
import EventTypeChip from "./EventTypeChip"
import FullCalendarEvent from "./FullCalendarEvent"
import SelectDatePopover from "./SelectDatePopover"
import TimeSliceButtonGroup, { TimeSliceButton } from "./TimeSliceButtonGroup"
import "./fullcalendar-custom.css"

export default function CandidatesCalendar({
  onIsLoadingChanged,
  currentDate,
  selectedTimeSlice,
  selectedEventType,
}) {
  const [events, setEvents] = useState([])
  const calendarRef = useRef(null)
  const [clickedEvent, setClickedEvent] = useState(null)
  const axios = useAuthorizedAxiosClient()
  const isMobile = useIsMobile()

  function handleEventClicked(info) {
    setClickedEvent(info)
  }

  function getCalendarAPI() {
    return calendarRef.current.getApi()
  }

  function makeCalendarEvent(date, title, event) {
    return {
      start: date,
      end: addHours(new Date(date), 1).toISOString(),
      title,
      extendedProps: event,
    }
  }

  useEffect(() => {
    onIsLoadingChanged(true)
    const searchParams = new URLSearchParams()
    searchParams.append(
      "start_date",
      formatISO(currentDate, { representation: "date" })
    )
    searchParams.append("time_slice", selectedTimeSlice.toLowerCase())
    searchParams.append("type", selectedEventType.toLowerCase())
    axios
      .get(`/clients/api/candidates-calendar/?${searchParams.toString()}`)
      .then(
        ({
          data: { booking_logs, meeting_schedules, calls, email_send_events },
        }) => {
          let calendarEvents = booking_logs.map((bookingLog) =>
            makeCalendarEvent(bookingLog.stamp, "Meeting", bookingLog)
          )
          calendarEvents = calendarEvents.concat(
            meeting_schedules.map((meetingSchedule) =>
              makeCalendarEvent(
                meetingSchedule.start_datetime,
                "Meeting",
                meetingSchedule
              )
            )
          )
          calendarEvents = calendarEvents.concat(
            calls.map((call) => makeCalendarEvent(call.created, "Call", call))
          )
          calendarEvents = calendarEvents.concat(
            email_send_events.map((emailSendEvent) =>
              makeCalendarEvent(emailSendEvent.created, "Email", emailSendEvent)
            )
          )
          setEvents(calendarEvents)
        }
      )
      .finally(() => onIsLoadingChanged(false))
    // eslint-disable-next-line
  }, [currentDate, selectedTimeSlice, selectedEventType])

  useEffect(() => {
    getCalendarAPI().changeView(
      {
        [TIME_SLICE_MONTH]: "dayGridMonth",
        [TIME_SLICE_WEEK]: "timeGridWeek",
        [TIME_SLICE_DAY]: "timeGridDay",
      }[selectedTimeSlice]
    )
  }, [selectedTimeSlice])

  useEffect(() => {
    getCalendarAPI().gotoDate(currentDate)
  }, [currentDate])

  return (
    <Box sx={{ pb: 1 }}>
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        themeSystem="standard"
        height={isMobile ? "calc(100vh - 190px)" : "calc(100vh - 300px)"}
        headerToolbar={{
          start: "",
          center: "",
          end: "",
          right: "",
        }}
        dayHeaderFormat={{
          weekday: isMobile ? "narrow" : "long",
        }}
        displayEventTime
        events={events}
        eventClick={handleEventClicked}
        eventContent={FullCalendarEvent}
        eventColor="transparent"
        ref={calendarRef}
      />
      <EventPopover
        anchorElem={clickedEvent?.el}
        eventTarget={clickedEvent?.event?.extendedProps}
        onClose={() => setClickedEvent(null)}
      />
    </Box>
  )
}

export function CalendarControls({
  selectedEventName,
  setSelectedEventName,
  selectedTimeSlice,
  setSelectedTimeSlice,
  currentDate,
  setCurrentDate,
}) {
  const [selectCurrentDatePopoverRef, setSelectCurrentDatePopoverRef] =
    useState(null)
  return (
    <RowButColumnOnMobile
      sx={{
        flexDirection: "row-reverse",
        justifyContent: { xs: "flex-start", sm: "space-between" },
        alignItems: "center",
        width: "100%",
        gap: 2,
      }}
    >
      <Row
        sx={{
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <TimeSliceButtonGroup size="small" color="inherit">
          {[TIME_SLICE_MONTH, TIME_SLICE_WEEK, TIME_SLICE_DAY].map(
            (sliceName, i) => (
              <TimeSliceButton
                className={
                  sliceName === selectedTimeSlice ? "active" : undefined
                }
                onClick={() => setSelectedTimeSlice(sliceName)}
                key={i}
              >
                {sliceName}
              </TimeSliceButton>
            )
          )}
        </TimeSliceButtonGroup>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<CalendarTodayOutlinedIcon />}
          onClick={(e) => setSelectCurrentDatePopoverRef(e.currentTarget)}
          sx={{
            width: { xs: "100%", sm: "initial" },
          }}
        >
          {format(currentDate, "MMMM y")}
        </Button>
        <SelectDatePopover
          anchorElem={selectCurrentDatePopoverRef}
          onClose={() => setSelectCurrentDatePopoverRef(null)}
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
        />
      </Row>
      <Row
        sx={{
          gap: { xs: 1, sm: 1 },
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        {EVENT_TYPES.map((event, i) => (
          <EventTypeChip
            sx={{
              width: { xs: "calc(50% - 8px)", sm: "initial" },
            }}
            key={i}
            icon={<event.icon />}
            label={event.name}
            className={event.name === selectedEventName ? "active" : undefined}
            onClick={() => setSelectedEventName(event.name)}
          />
        ))}
      </Row>
    </RowButColumnOnMobile>
  )
}

function gapiLoaded() {
  // eslint-disable-next-line no-undef
  gapi.load("client")
}

let tokenClient

function gsiLoaded() {
  // eslint-disable-next-line no-undef
  tokenClient = google.accounts.oauth2.initTokenClient({
    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: "https://www.googleapis.com/auth/calendar.events",
    callback: "", // defined later
  })
}

function loadGoogleScript(scriptSRC, onLoad) {
  const googleScriptElement = document.createElement("script")
  googleScriptElement.async = true
  googleScriptElement.defer = true
  googleScriptElement.src = scriptSRC
  googleScriptElement.onload = onLoad
  document.body.appendChild(googleScriptElement)
}

if (!tokenClient) {
  loadGoogleScript("https://apis.google.com/js/api.js", gapiLoaded)
  loadGoogleScript("https://accounts.google.com/gsi/client", gsiLoaded)
}

export function requestCalendarAccess() {
  return new Promise((resolve, reject) => {
    tokenClient.callback = async (resp) => {
      if (resp.error !== undefined) {
        reject(resp.error)
      }
      // eslint-disable-next-line no-undef
      resolve(gapi.client.getToken().access_token)
    }
    // eslint-disable-next-line no-undef
    if (gapi.client.getToken() === null) {
      tokenClient.requestAccessToken({ prompt: "consent" })
    } else {
      // Skip display of account chooser and consent dialog for an existing session.
      tokenClient.requestAccessToken({ prompt: "" })
    }
  })
}

export const EVENT_NAME_ALL = "All"
export const TIME_SLICE_MONTH = "Month"
const TIME_SLICE_WEEK = "Week"
const TIME_SLICE_DAY = "Day"

const EVENT_TYPES = [
  {
    name: EVENT_NAME_ALL,
    icon: ViewCompactIcon,
  },
  {
    name: "Call",
    icon: PhoneOutlinedIcon,
  },
  {
    name: "Meeting",
    icon: CalendarTodayOutlinedIcon,
  },
  {
    name: "Email",
    icon: EmailOutlinedIcon,
  },
]
